import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelector } from "@/components/ui/searchableSelector";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { User, FileText, Settings } from "lucide-react";
import type {
	OrgaAdminUser,
	GruppeKlausurDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";
import { useKorrekturStepAssignmentOperations } from "../contexts/korrekturStepAssignment.context";

interface AssignmentPopupProps {
	user: OrgaAdminUser;
	klausuren: GruppeKlausurDTO[];
	onClose: () => void;
	onNavigateToAssignment?: () => void;
}

/**
 * AssignmentPopup component for assigning a korrektor to a klausur and step
 * @param user - The user to assign the korrektor to
 * @param klausuren - The klausuren to assign the korrektor to
 * @param onClose - The function to call when the popup is closed
 * @param onNavigateToAssignment - The function to call when the assignment is made
 */
export function AssignmentPopup({
	user,
	klausuren,
	onClose,
	onNavigateToAssignment,
}: AssignmentPopupProps) {
	const [selectedKlausur, setSelectedKlausur] = useState<string>("");
	const [selectedStep, setSelectedStep] = useState<string>("");

	// Get assignment context operations
	const { setAssignmentFilters, availableStepNumbers } =
		useKorrekturStepAssignmentOperations();

	// Format step numbers for the SearchableSelector component
	const korrekturSteps = availableStepNumbers.map((stepNr) => ({
		value: stepNr.toString(),
		label: `Korrektur-Schritt ${stepNr}`,
	}));

	const handleAssign = () => {
		if (!selectedKlausur || !selectedStep) {
			return;
		}

		// Find the selected klausur title for search
		const selectedKlausurObj = klausuren.find((k) => k.id === selectedKlausur);

		// Set filters in context
		setAssignmentFilters({
			searchTerm: selectedKlausurObj?.title,
			korrektorId: user.id,
			stepNumber: Number.parseInt(selectedStep, 10),
			openBulkAssignment: true,
		});

		// Trigger navigation callback
		onNavigateToAssignment?.();

		onClose();
	};

	const isValid = selectedKlausur && selectedStep;

	return (
		<Dialog open={true} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-md border-blue-200 bg-background-secondary">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<User className="h-5 w-5 text-blue-600" />
						Korrektor zuweisen
					</DialogTitle>
					<DialogDescription>
						Wählen die Klausur und den Korrekturschritt für{" "}
						<span className="font-medium">{user.name || user.email}</span> aus.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* User Info */}
					<div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg ">
						<div className="bg-blue-100 p-2 rounded-full">
							<User className="h-4 w-4 text-blue-600" />
						</div>
						<div>
							<p className="font-medium text-sm">{user.name || "Unbekannt"}</p>
							<p className="text-xs text-muted-foreground">{user.email}</p>
						</div>
					</div>

					{/* Klausur Selection */}
					<div className="space-y-2">
						<Label className="flex items-center gap-2">
							<FileText className="h-4 w-4" />
							Klausur auswählen
						</Label>
						<SearchableSelector
							options={klausuren.map((klausur) => ({
								value: klausur.id,
								label: klausur.title || "Unbekannte Klausur",
							}))}
							value={selectedKlausur}
							onChange={setSelectedKlausur}
							placeholder="Klausur auswählen..."
							searchPlaceholder="Klausur suchen..."
							emptyMessage="Keine Klausuren gefunden"
							className="w-full border border-blue-200  rounded-lg"
						/>
					</div>

					{/* Step Selection */}
					<div className="space-y-2">
						<Label className="flex items-center gap-2">
							<Settings className="h-4 w-4" />
							Korrekturschritt auswählen
						</Label>
						<SearchableSelector
							options={korrekturSteps}
							value={selectedStep}
							onChange={setSelectedStep}
							placeholder={
								korrekturSteps.length === 0
									? "Keine Schritte verfügbar..."
									: "Korrektur-Schritt auswählen..."
							}
							searchPlaceholder="Schritt suchen..."
							emptyMessage="Keine Korrekturschritte gefunden"
							disabled={korrekturSteps.length === 0}
							className="w-full border border-blue-200 rounded-lg"
						/>
					</div>
				</div>

				<DialogFooter className="flex gap-2">
					<Button variant="outline" onClick={onClose}>
						Abbrechen
					</Button>
					<Button
						onClick={handleAssign}
						disabled={!isValid}
						className="bg-blue-600 hover:bg-blue-700"
					>
						Zur Zuweisung
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
